import React, { useState, useEffect, useRef } from 'react';
import { usersAPI } from '../../utils/api';
import './CandidateSelector.css';

/**
 * CandidateSelector Component
 *
 * Intelligent candidate search and selection with:
 * - Real-time search with debouncing
 * - Suggested candidates based on recent activity
 * - Pagination for large datasets
 * - Keyboard navigation
 * - Optimized for 10,000s of users
 *
 * @param {string} value - Selected candidate ID
 * @param {function} onChange - Callback when candidate is selected (receives candidateId)
 * @param {function} onSelectCandidate - Optional callback that receives full candidate object
 * @param {string} error - Error message to display
 */
const CandidateSelector = ({ value, onChange, onSelectCandidate, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [suggestedCandidates, setSuggestedCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [inviteError, setInviteError] = useState('');
  const [inviting, setInviting] = useState(false);

  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Load suggested candidates on mount
  useEffect(() => {
    loadSuggestedCandidates();
  }, []);

  // Load selected candidate details if value is provided
  useEffect(() => {
    if (value && !selectedCandidate) {
      loadCandidateById(value);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length >= 2) {
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for debounced search
      searchTimeoutRef.current = setTimeout(() => {
        searchCandidates(searchQuery);
      }, 300); // 300ms debounce

      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    } else if (searchQuery.length === 0) {
      setCandidates([]);
    }
  }, [searchQuery]);

  const loadSuggestedCandidates = async () => {
    try {
      // Get recent candidates (could be based on recent assessments, activity, etc.)
      const response = await usersAPI.getAll({
        role: 'candidate',
        limit: 5,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      setSuggestedCandidates(response.data.users || []);
    } catch (error) {
      console.error('[CandidateSelector] Failed to load suggestions:', error);
    }
  };

  const searchCandidates = async (query) => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll({
        role: 'candidate',
        search: query,
        limit: 20
      });
      setCandidates(response.data.users || []);
    } catch (error) {
      console.error('[CandidateSelector] Search failed:', error);
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCandidateById = async (candidateId) => {
    try {
      const response = await usersAPI.getById(candidateId);
      setSelectedCandidate(response.data.user);
    } catch (error) {
      console.error('[CandidateSelector] Failed to load candidate:', error);
    }
  };

  const handleSelect = (candidate) => {
    setSelectedCandidate(candidate);
    onChange(candidate._id);
    if (onSelectCandidate) {
      onSelectCandidate(candidate); // Pass full candidate object
    }
    setIsOpen(false);
    setSearchQuery('');
    setCandidates([]);
  };

  const handleClear = () => {
    setSelectedCandidate(null);
    onChange('');
    setSearchQuery('');
    setCandidates([]);
    setIsOpen(true);
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    const items = searchQuery ? candidates : suggestedCandidates;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => (prev < items.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      handleSelect(items[focusedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleInviteCandidate = async () => {
    // Validate
    if (!inviteFormData.name.trim()) {
      setInviteError('Name is required');
      return;
    }
    if (!inviteFormData.email.trim()) {
      setInviteError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteFormData.email)) {
      setInviteError('Invalid email format');
      return;
    }

    setInviting(true);
    setInviteError('');

    try {
      // Create new candidate user
      const response = await usersAPI.create({
        name: inviteFormData.name,
        email: inviteFormData.email,
        phone: inviteFormData.phone,
        role: 'candidate',
        status: 'registered'
      });

      const newCandidate = response.data.user;

      // Select the newly created candidate
      handleSelect(newCandidate);

      // Close invite panel
      setShowInvitePanel(false);

      // Reset form
      setInviteFormData({ name: '', email: '', phone: '' });

    } catch (error) {
      console.error('[CandidateSelector] Failed to create candidate:', error);
      setInviteError(error.response?.data?.error || 'Failed to create candidate. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  const handleInviteFormChange = (field, value) => {
    setInviteFormData(prev => ({ ...prev, [field]: value }));
    setInviteError('');
  };

  const displayItems = searchQuery ? candidates : suggestedCandidates;

  return (
    <>
      <div className="candidate-selector" ref={dropdownRef}>
      {selectedCandidate ? (
        // Selected candidate display
        <div className={`candidate-selected ${error ? 'error' : ''}`}>
          <div className="candidate-selected-info">
            <div className="candidate-avatar">
              {selectedCandidate.name?.charAt(0).toUpperCase() || 'C'}
            </div>
            <div className="candidate-details">
              <div className="candidate-name">{selectedCandidate.name || 'Unknown'}</div>
              <div className="candidate-email">{selectedCandidate.email}</div>
            </div>
          </div>
          <button
            type="button"
            className="candidate-clear-btn"
            onClick={handleClear}
            aria-label="Clear selection"
          >
            ✕
          </button>
        </div>
      ) : (
        // Search input
        <div className="candidate-search-wrapper">
          <input
            ref={searchRef}
            type="text"
            className={`candidate-search-input ${error ? 'error' : ''}`}
            placeholder="Search candidates by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
          <svg className="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM18 18l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}

      {/* Dropdown */}
      {isOpen && !selectedCandidate && (
        <div className="candidate-dropdown">
          {loading ? (
            <div className="candidate-loading">
              <div className="loading-spinner"></div>
              <span>Searching candidates...</span>
            </div>
          ) : displayItems.length > 0 ? (
            <>
              {!searchQuery && (
                <div className="dropdown-section-header">
                  Suggested Candidates
                </div>
              )}
              {searchQuery && candidates.length > 0 && (
                <div className="dropdown-section-header">
                  {candidates.length} candidate{candidates.length !== 1 ? 's' : ''} found
                </div>
              )}
              <div className="candidate-list">
                {displayItems.map((candidate, index) => (
                  <button
                    key={candidate._id}
                    type="button"
                    className={`candidate-item ${focusedIndex === index ? 'focused' : ''}`}
                    onClick={() => handleSelect(candidate)}
                    onMouseEnter={() => setFocusedIndex(index)}
                  >
                    <div className="candidate-avatar">
                      {candidate.name?.charAt(0).toUpperCase() || 'C'}
                    </div>
                    <div className="candidate-info">
                      <div className="candidate-name">{candidate.name || 'Unknown'}</div>
                      <div className="candidate-email">{candidate.email}</div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : searchQuery.length >= 2 ? (
            <div className="candidate-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p>No candidates found</p>
              <span>Try searching by name or email</span>
            </div>
          ) : (
            <div className="candidate-empty">
              <p>Type to search candidates</p>
              <span>Enter at least 2 characters</span>
            </div>
          )}

          {/* Invite New Candidate Button */}
          <div className="invite-candidate-section">
            <button
              type="button"
              className="invite-candidate-btn"
              onClick={() => {
                setShowInvitePanel(true);
                setIsOpen(false);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 5v10M5 10h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Invite New Candidate
            </button>
          </div>
        </div>
      )}

      {error && <span className="error-message">{error}</span>}
    </div>

    {/* Invite Panel - Slides in from right */}
    {showInvitePanel && (
      <>
        <div className="invite-form-overlay" onClick={() => setShowInvitePanel(false)} />
        <div className="invite-form-panel">
          <div className="invite-form-header">
            <h3>Invite New Candidate</h3>
            <button
              type="button"
              className="invite-form-close"
              onClick={() => setShowInvitePanel(false)}
            >
              ✕
            </button>
          </div>

          <div className="invite-form-content">
            <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
              Create a new candidate account. They will receive an email with login instructions.
            </p>

            {inviteError && (
              <div className="notification error-notification" style={{ marginBottom: '1.5rem' }}>
                <span className="notification-message">{inviteError}</span>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="invite-name">Full Name *</label>
              <input
                type="text"
                id="invite-name"
                className="form-input"
                placeholder="e.g., John Doe"
                value={inviteFormData.name}
                onChange={(e) => handleInviteFormChange('name', e.target.value)}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="invite-email">Email Address *</label>
              <input
                type="email"
                id="invite-email"
                className="form-input"
                placeholder="e.g., john.doe@company.com"
                value={inviteFormData.email}
                onChange={(e) => handleInviteFormChange('email', e.target.value)}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="invite-phone">Phone Number (Optional)</label>
              <input
                type="tel"
                id="invite-phone"
                className="form-input"
                placeholder="e.g., +1 (555) 123-4567"
                value={inviteFormData.phone}
                onChange={(e) => handleInviteFormChange('phone', e.target.value)}
              />
            </div>
          </div>

          <div className="invite-form-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowInvitePanel(false)}
              disabled={inviting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleInviteCandidate}
              disabled={inviting}
            >
              {inviting ? 'Creating...' : 'Create & Select'}
            </button>
          </div>
        </div>
      </>
    )}
    </>
  );
};

export default CandidateSelector;
